import React, { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as util from '../../utils/urlParams';
import { ConnectedMainDisplayBoxWithRouter } from '../MainDisplayBox';
import { SmallDisplayBox } from '../SmallDisplayBox';
import { BuildingActions, UtilityActions } from '../../store';
import { SelectedBuilding, NavigationParamsType } from '../../store/building/types';
import { GraphDateRange, UtilityOverview } from '../../store/utility/types';
import { Error404Page } from '../Error404Page';

export interface Props {
  clearErrors(): void;
  invalidSelectedBuildingError: string;
  getUtilityOverview(url: string, weatherAdjusted: boolean, category?: string, subcategory?: string): Promise<any>;
  loading: boolean;
  selectedBuilding: SelectedBuilding;
  navigationParams: NavigationParamsType;
  electricalGraphDateRange: GraphDateRange;
  gasGraphDateRange: GraphDateRange;
  updateElectricalGraphDateRange(graphDateRange: GraphDateRange): void;
  updateGasGraphDateRange(graphDateRange: GraphDateRange): void;
  utilityOverview: UtilityOverview;
  weatherAdjusted: boolean;
  toggleWeatherAdjusted(toggleValue: boolean): void;
}

export declare type ComposedProps = Props & RouteComponentProps;

export class EnergyUsage extends Component<ComposedProps, never> {
  componentDidMount() {
    const { clearErrors, getUtilityOverview } = this.props;

    clearErrors();

    const {
      electricalGraphDateRange,
      gasGraphDateRange,
      updateElectricalGraphDateRange,
      updateGasGraphDateRange,
      location,
      navigationParams: {
        selectedCategory: { id: selectedCategoryId },
        selectedSubCategory: {
          name: selectedSubCategoryName,
          url: selectedSubCategoryUrl,
          api_location: selectedSubCategoryApiLocation,
        },
      },
      weatherAdjusted,
    } = this.props;

    if (!window.sessionStorage.getItem('electricalGraphDateRange')) {
      window.sessionStorage.setItem('electricalGraphDateRange', JSON.stringify(electricalGraphDateRange));
    }

    if (!window.sessionStorage.getItem('gasGraphDateRange')) {
      window.sessionStorage.setItem('gasGraphDateRange', JSON.stringify(gasGraphDateRange));
    }

    const electricalDateFromSession = window.sessionStorage.getItem('electricalGraphDateRange');
    const electricalDate: GraphDateRange = electricalDateFromSession
      ? JSON.parse(electricalDateFromSession)
      : electricalGraphDateRange;

    const gasDateFromSession = window.sessionStorage.getItem('gasGraphDateRange');
    const gasDate = gasDateFromSession ? JSON.parse(gasDateFromSession) : gasGraphDateRange;

    const graphDateRange = selectedCategoryId === 'electrical-usage' ? electricalDate : gasDate;

    let { search } = location;
    if (!search) {
      search = util.addSearchParams(graphDateRange);
      this.props.history.replace(`${selectedSubCategoryUrl}?${search}`);
    } else {
      const filter = util.getSavingsFilterFromUrlParams(search);
      if (selectedCategoryId === 'electrical-usage') {
        updateElectricalGraphDateRange(filter);
      }

      if (selectedCategoryId === 'gas-usage') {
        updateGasGraphDateRange(filter);
      }
    }

    return getUtilityOverview(
      selectedSubCategoryApiLocation,
      weatherAdjusted,
      selectedCategoryId,
      selectedSubCategoryName,
    );
  }

  componentDidUpdate(prevProps: Props) {
    const {
      weatherAdjusted,
      getUtilityOverview,
      navigationParams: {
        selectedCategory: { id: selectedCategoryId },
        selectedSubCategory: { name: selectedSubCategoryName, api_location: selectedSubCategoryApiLocation },
      },
    } = this.props;

    if (prevProps.weatherAdjusted !== weatherAdjusted) {
      return getUtilityOverview(
        selectedSubCategoryApiLocation,
        weatherAdjusted,
        selectedCategoryId,
        selectedSubCategoryName,
      );
    }
  }

  changeToggle = () => {
    const { toggleWeatherAdjusted, weatherAdjusted } = this.props;

    toggleWeatherAdjusted(!weatherAdjusted);
  };

  render() {
    const { loading, utilityOverview, invalidSelectedBuildingError, weatherAdjusted } = this.props;

    if (invalidSelectedBuildingError) {
      return <Error404Page />;
    }

    if (loading || Object.keys(utilityOverview).length === 0) {
      return (
        <React.Fragment>
          <ConnectedMainDisplayBoxWithRouter isLoading={true} />
          <SmallDisplayBox />
          <SmallDisplayBox />
        </React.Fragment>
      );
    }

    const {
      savings_data: { energy_usage: energyUsage = [], savings = [], graph_data: graphData = [] },
      unit = '',
      interval: reportUnit = 'day',
    } = utilityOverview;

    return (
      <React.Fragment>
        <ConnectedMainDisplayBoxWithRouter
          isLoading={false}
          unit={unit}
          graphData={graphData}
          reportUnit={reportUnit}
          weatherAdjusted={weatherAdjusted}
          toggleWeatherAdjusted={this.changeToggle}
        />
        <SmallDisplayBox contents={energyUsage} />
        <SmallDisplayBox contents={savings} />
      </React.Fragment>
    );
  }
}

export function mapStateToProps(state: any) {
  return {
    utilityOverview: state.utilityReducer.utilityOverview,
    loading: state.utilityReducer.isFetching,
    selectedBuilding: state.buildingReducer.selectedBuilding,
    navigationParams: state.buildingReducer.navigationParams,
    invalidSelectedBuildingError: state.buildingReducer.error,
    electricalGraphDateRange: state.utilityReducer.electricalGraphDateRange,
    gasGraphDateRange: state.utilityReducer.gasGraphDateRange,
    weatherAdjusted: state.utilityReducer.weatherAdjusted,
  };
}

const mapDispatchToProps = {
  getUtilityOverview: UtilityActions.getUtilityOverview,
  updateElectricalGraphDateRange: UtilityActions.updateElectricalGraphDateRange,
  updateGasGraphDateRange: UtilityActions.updateGasGraphDateRange,
  toggleWeatherAdjusted: UtilityActions.toggleWeatherAdjusted,
  clearErrors: BuildingActions.clearSelectedBuildingError,
};

export const ConnectedEnergyUsageWithRouter = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
)(EnergyUsage);
